import { type FunctionComponent, type ReactNode, useState } from 'react';

import { Input, type Theme } from '@dr.pogodin/react-utils';

import { useArtistList } from 'hooks';
import { type ArtistT, getId } from 'utils';

import IconClose from 'assets/images/icon-cross-24px.svg';

import MessageModal from '../MessageModal';
import AdminPractitionerLink from '../PractitionerLink';

import searchTheme from './search.theme.scss';
import S from './style.scss';

type PropsT = {
  emptyLabel?: string;
  noTransferConfirmation?: boolean;
  onChange: (practitioner: ArtistT | ArtistT[] | undefined) => void;
  practitioner?: ArtistT | ArtistT[];
};

export const AdminPractitionerSelect: FunctionComponent<PropsT> = ({
  emptyLabel,
  noTransferConfirmation,
  onChange,
  practitioner,
}) => {
  const [search, setSearch] = useState('');

  const [newSelection, setNewSelection] = useState<ArtistT>();

  const suggestions = useArtistList({ textSearch: search });

  const isMultiple = Array.isArray(practitioner);
  let practitionerArray: ArtistT[] = [];
  if (isMultiple) {
    practitionerArray = practitioner;
  } else if (practitioner) {
    practitionerArray = [practitioner];
  }

  const suggestionNodes: ReactNode[] = [];

  // Always show suggestions
  for (const item of suggestions.items) {
    const isSelected = practitionerArray.some((p) => p.id === item.id);
    suggestionNodes.push(
      <div
        key={getId(item)}
        onClick={() => {
          if (isSelected) {
            return;
          }

          if (isMultiple) {
            onChange([...practitionerArray, item]);
          } else if (noTransferConfirmation) {
            onChange(item);
          } else {
            setNewSelection(item);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (isSelected) {
              return;
            }

            if (isMultiple) {
              onChange([...practitionerArray, item]);
            } else if (noTransferConfirmation) {
              onChange(item);
            } else {
              setNewSelection(item);
            }
          }
        }}
        role="button"
        styleName={isSelected ? 'selectedItem' : 'noStyleLink'}
        tabIndex={0}
      >
        <AdminPractitionerLink
          disabled
          key={getId(item)}
          practitioner={item}
        />
      </div>,
    );
  }
  const isNoSuggestionNodes = !suggestionNodes.length;
  if (isNoSuggestionNodes) {
    suggestionNodes.push(
      <div key="empty" styleName="nobodyFound">
        Nobody found
      </div>,
    );
  }

  return (
    <div className={S.container}>
      <div styleName="selectedTags">
        {practitionerArray.map((p) => (
          <div key={p.id} styleName="selectedTag">
            <AdminPractitionerLink
              disabled
              practitioner={p}
            />
            <button
              onClick={() => {
                if (isMultiple) {
                  onChange(practitionerArray.filter(
                    (item) => item.id !== p.id,
                  ));
                } else {
                  onChange(undefined);
                }
              }}
              styleName="removeButton"
              type="button"
            >
              <IconClose />
            </button>
          </div>
        ))}
      </div>
      <Input
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        placeholder={emptyLabel ?? 'Search Practitioner'}
        theme={searchTheme as Theme<string>}
        value={search}
      />
      <div styleName="suggestions">
        {suggestionNodes}
      </div>
      {newSelection ? (
        <MessageModal
          confirmButtonLabel="Yes, transfer"
          icon="WARN"
          onCancel={() => {
            setNewSelection(undefined);
          }}
          onConfirm={() => {
            onChange(newSelection);
            setNewSelection(undefined);
          }}
        >
          Do you want to transfer the video ownership
          {practitioner && !isMultiple ? (
            <>
              from
              <AdminPractitionerLink practitioner={practitioner} />
            </>
          ) : null}
          to
          <AdminPractitionerLink practitioner={newSelection} />
          Are you sure?
        </MessageModal>
      ) : null}
    </div>
  );
};
